// Disponibilidad / calendario (RentalOS F6) — anti sobre-venta.
//
// Responde la pregunta que el vendedor hace ANTES de prometer maquinaria:
// "¿qué tengo libre del 5 al 12?". Sin esto se promete equipo que ya está
// comprometido, y eso cuesta el cliente.
//
// Ojo con la resta: total = ocupadas + fuera de servicio + libres. Lo que está
// en mantenimiento o reparación NO cuenta como rentable aunque no esté rentado
// — por eso se muestra aparte y no inflamos el número de disponibles.

import { useEffect, useMemo, useState } from 'react';

import { checkAvailability } from '../api';
import type { Availability } from '../types';

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00`);
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
};

export function AvailabilityPage() {
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(() => plusDays(today(), 7));
  const [category, setCategory] = useState('');
  const [data, setData] = useState<Availability | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (!start || !end) return;
    if (end < start) { setError('La fecha final no puede ser menor que la inicial.'); return; }
    setLoading(true);
    setError(null);
    try {
      setData(await checkAvailability({ start_date: start, end_date: end, category: category || undefined }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo consultar disponibilidad');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Agrupar por categoría: el vendedor pregunta por tipo de máquina
  // ("¿tienes torres de iluminación?"), no por número de serie.
  const byCategory = useMemo(() => {
    const m = new Map<string, Availability['available_units']>();
    for (const u of data?.available_units || []) {
      const arr = m.get(u.category) || [];
      arr.push(u);
      m.set(u.category, arr);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);

  const field = 'rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Desde</label>
            <input type="date" className={field} value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Hasta</label>
            <input type="date" className={field} value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Categoría</label>
            <input
              className={field}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Todas"
            />
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {loading ? 'Consultando…' : 'Consultar'}
          </button>
          <div className="flex gap-1">
            {[7, 15, 30].map((d) => (
              <button
                key={d}
                onClick={() => { setEnd(plusDays(start, d)); }}
                className="rounded-md border border-border px-2 py-2 text-xs text-muted-foreground hover:bg-muted"
                title={`${d} días desde el inicio`}
              >
                +{d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      ) : null}

      {data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground">Libres en el periodo</div>
              <div className="mt-1 text-2xl font-semibold text-emerald-600 dark:text-emerald-400">{data.available}</div>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground">Comprometidas</div>
              <div className="mt-1 text-2xl font-semibold text-foreground">{data.occupied}</div>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground">Fuera de servicio</div>
              <div className="mt-1 text-2xl font-semibold text-amber-600 dark:text-amber-400">{data.out_of_service}</div>
              <div className="mt-1 text-xs text-muted-foreground">No cuentan como rentables</div>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-xs text-muted-foreground">Flota total</div>
              <div className="mt-1 text-2xl font-semibold text-foreground">{data.total_units}</div>
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card">
            <div className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
              Disponible del {data.start_date} al {data.end_date}
            </div>
            {byCategory.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                No hay unidades libres en ese rango{category ? ` para "${category}"` : ''}.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {byCategory.map(([cat, units]) => (
                  <div key={cat} className="px-4 py-3">
                    <div className="mb-1.5 flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">{cat}</span>
                      <span className="text-sm text-muted-foreground">{units.length} libre(s)</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {units.map((u) => (
                        <span
                          key={u.id}
                          className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
                          title={u.identifier}
                        >
                          {u.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
